import {
  AlertOutlined,
  LogoutOutlined,
  MailOutlined,
  TagsOutlined,
  UserOutlined,
} from "@ant-design/icons";
import type { GetProp, MenuProps } from "antd";

export type MenuItem = GetProp<MenuProps, "items">[number];

/** Menu key that triggers logout instead of navigation. */
export const LOGOUT_KEY = "logout";

export interface MenuAccess {
  isAdmin: boolean;
  isRestrictedEmailUser: boolean;
}

/**
 * Menu tree whose leaf keys ARE route paths (plus LOGOUT_KEY).
 * Restricted-email users see only Movie Ticket; admins additionally get
 * User and Marketing Studio.
 */
export function buildMenuItems({ isAdmin, isRestrictedEmailUser }: MenuAccess): MenuItem[] {
  if (isRestrictedEmailUser) {
    return [
      {
        key: "tickets",
        label: "Tickets",
        icon: <TagsOutlined />,
        children: [
          {
            key: "sub-manage",
            label: "Manage Ticket",
            children: [{ key: "/movies", label: "Movie Ticket" }],
          },
        ],
      },
      { key: LOGOUT_KEY, icon: <LogoutOutlined />, label: "Log out" },
    ];
  }

  const items: MenuItem[] = [
    {
      key: "tickets",
      label: "Tickets",
      icon: <TagsOutlined />,
      children: [
        { key: "/reports", label: "Dashboard" },
        { key: "/tickets", label: "Ticket Code" },
        { key: "/ticket-status", label: "Ticket Status" },
        {
          key: "sub-manage",
          label: "Manage Ticket",
          children: [
            { key: "/movies", label: "Movie Ticket" },
            { key: "/liveTickets", label: "Live Ticket" },
            { key: "/collections", label: "Collection Ticket" },
            { key: "/museum-passes", label: "Museum Passes" },
          ],
        },
      ],
    },
  ];

  if (isAdmin) {
    items.push({ key: "/users", icon: <UserOutlined />, label: "User" });
    items.push({
      key: "marketing",
      icon: <MailOutlined />,
      label: "Marketing Studio",
      children: [
        { key: "/email-studio/templates", label: "Templates" },
        { key: "/email-studio/campaigns", label: "Campaigns" },
        { key: "/email-studio/analytics", label: "Analytics" },
      ],
    });
  }

  items.push({ key: "/error-reports", icon: <AlertOutlined />, label: "Error Reports" });
  items.push({ key: LOGOUT_KEY, icon: <LogoutOutlined />, label: "Log out" });

  return items;
}

/** Selected menu key(s) for the current location, mapping editor sub-paths
 *  to their parent list pages. */
export function selectedKeyForPath(pathname: string): string[] {
  if (pathname.startsWith("/email-studio/templates")) return ["/email-studio/templates"];
  if (pathname.startsWith("/email-studio/campaigns")) return ["/email-studio/campaigns"];
  return [pathname];
}
